import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Crown } from 'lucide-react';
import type { BattleRoomState } from '../../../types/battleRush';
import { METRIC_DEFINITIONS } from '../../../constants/metricDefinitions';

const METRIC_KEY_MAP: Record<string, string> = {
  'Market Cap': 'marketCapitalization',
  'Annual Revenue': 'annualRevenue',
  'Net Income': 'netIncome',
  'Gross Profit Margin': 'grossMargin',
  'Net Margin': 'profitMargin',
  'P/E Ratio': 'trailingPE',
};

interface BattleResultsProps {
  room: BattleRoomState;
  currentUserId: string;
  isHost: boolean;
  onRestart: () => void | Promise<void>;
}

export const BattleResults: React.FC<BattleResultsProps> = ({ room, currentUserId, isHost, onRestart }) => {
  const [copied, setCopied] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);

  const isDraw = !room.winnerUserId;

  const sortedPlayers = [...room.players].sort((a, b) => {
    // Winner first, then by score desc, then by lives desc
    if (a.userId === room.winnerUserId) return -1;
    if (b.userId === room.winnerUserId) return 1;
    if (b.score !== a.score) return b.score - a.score;
    return b.lives - a.lives;
  });

  const winner = sortedPlayers[0];
  const isWinner = !isDraw && winner?.userId === currentUserId;

  const handleShareResults = useCallback(async () => {
    const myPlayer = room.players.find((p) => p.userId === currentUserId);
    const myScore = myPlayer?.score ?? 0;
    const text = isDraw
      ? `Trivia Rush Battle ended in a draw at ${myScore}! Try it: wallstreetle.com/trivia-rush`
      : isWinner
        ? `I won a Trivia Rush Battle with a score of ${myScore}! Challenge me: wallstreetle.com/trivia-rush`
        : `I scored ${myScore} in Trivia Rush Battle! Try it: wallstreetle.com/trivia-rush`;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [room.players, currentUserId, isDraw, isWinner]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center">
      {/* Winner announcement */}
      <div className="mb-6">
        <Crown
          className="w-12 h-12 mx-auto mb-3"
          style={{ color: isDraw ? 'var(--rush-text-muted)' : '#fbbf24' }}
          fill={isDraw ? 'var(--rush-text-muted)' : '#fbbf24'}
        />
        <h2
          className="text-3xl sm:text-4xl font-bold tracking-tight mb-1"
          style={{
            color: isDraw ? 'var(--rush-text-muted)' : isWinner ? 'var(--rush-correct)' : 'var(--rush-text-primary)',
            fontFamily: 'DM Serif Display, serif',
          }}
        >
          {isDraw ? 'DRAW!' : isWinner ? 'YOU WIN!' : `${winner?.username || 'Unknown'} Wins!`}
        </h2>
        <p className="text-sm" style={{ color: 'var(--rush-text-muted)' }}>
          {isDraw ? `Tied at ${sortedPlayers[0]?.score ?? 0}` : `Final score: ${winner?.score ?? 0}`}
        </p>
      </div>

      {/* Standings */}
      <div className="w-full max-w-xs mb-8">
        <p
          className="text-xs uppercase tracking-wider mb-3 text-left"
          style={{ color: 'var(--rush-text-muted)' }}
        >
          Final Standings
        </p>
        <div className="flex flex-col gap-1.5">
          {sortedPlayers.map((player, index) => {
            const isMe = player.userId === currentUserId;
            const isFirst = index === 0;

            return (
              <div
                key={player.userId}
                className="flex items-center justify-between rounded-lg px-3 py-2"
                style={{
                  backgroundColor: isMe ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                  border: isMe ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid transparent',
                }}
              >
                <div className="flex items-center gap-2.5">
                  <span
                    className="text-sm font-mono font-bold w-5"
                    style={{
                      color: isFirst ? '#fbbf24' : index === 1 ? '#94a3b8' : index === 2 ? '#cd7f32' : 'var(--rush-text-muted)',
                    }}
                  >
                    {index === 0 && <Trophy className="w-4 h-4 inline" />}
                    {index > 0 && `#${index + 1}`}
                  </span>
                  <span
                    className="text-sm font-medium"
                    style={{ color: 'var(--rush-text-primary)' }}
                  >
                    {player.username}
                    {isMe && <span className="text-xs ml-1 opacity-60">(you)</span>}
                  </span>
                </div>
                <span
                  className="text-sm font-mono font-bold"
                  style={{ color: 'var(--rush-text-primary)' }}
                >
                  {player.score}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 w-full max-w-xs">
        <button
          onClick={handleShareResults}
          className="flex items-center justify-center gap-2 px-10 py-4 rounded-full text-lg font-bold transition-all duration-200 hover:scale-105 active:scale-95"
          style={{
            backgroundColor: copied ? '#16a34a' : '#22c55e',
            color: '#fff',
            boxShadow: '0 0 20px rgba(34, 197, 94, 0.4)',
          }}
        >
          {copied ? 'COPIED!' : 'SHARE RESULTS'}
        </button>

        {isHost ? (
          <button
            onClick={async () => {
              setIsRestarting(true);
              try { await onRestart(); } finally { setIsRestarting(false); }
            }}
            disabled={isRestarting}
            className="flex items-center justify-center gap-2 px-10 py-4 rounded-full text-lg font-bold transition-all duration-200 disabled:opacity-40 hover:scale-105 active:scale-95"
            style={{
              backgroundColor: 'var(--rush-purple-mid)',
              color: 'var(--rush-text-primary)',
              boxShadow: isRestarting ? undefined : 'var(--rush-shadow-glow)',
            }}
          >
            {isRestarting ? (
              <>
                <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                RESTARTING...
              </>
            ) : (
              'BACK TO LOBBY'
            )}
          </button>
        ) : (
          <button
            disabled
            className="w-full py-3 rounded-xl text-sm font-bold tracking-widest uppercase"
            style={{
              backgroundColor: 'var(--rush-purple-mid)',
              color: 'var(--rush-text-primary)',
              opacity: 0.5,
              cursor: 'not-allowed',
            }}
          >
            Waiting for host to restart...
          </button>
        )}

        <Link
          to="/trivia-rush"
          className="text-sm no-underline text-center transition-opacity hover:opacity-80"
          style={{ color: 'var(--rush-text-muted)' }}
        >
          Leave Battle
        </Link>
      </div>

      {/* TIP */}
      {room.currentQuestion?.metricDisplayName &&
        METRIC_KEY_MAP[room.currentQuestion.metricDisplayName] &&
        METRIC_DEFINITIONS[METRIC_KEY_MAP[room.currentQuestion.metricDisplayName]] && (
        <div
          className="w-full max-w-xs rounded-xl px-4 py-3 text-left text-sm leading-relaxed mt-8"
          style={{
            backgroundColor: 'rgba(96, 165, 250, 0.12)',
            border: '1px solid rgba(96, 165, 250, 0.25)',
            color: 'var(--rush-text-secondary, #cbd5e1)',
          }}
        >
          <span style={{ color: 'rgb(96, 165, 250)', fontWeight: 700 }}>TIP: </span>
          <strong>{room.currentQuestion.metricDisplayName}</strong> —{' '}
          {METRIC_DEFINITIONS[METRIC_KEY_MAP[room.currentQuestion.metricDisplayName]].explanation}
        </div>
      )}

      {/* Industry Insight */}
      {(room.roundReveal?.industryContextA || room.roundReveal?.industryContextB) && room.currentQuestion && (
        <div
          className="w-full max-w-xs rounded-xl px-4 py-3 text-left text-sm leading-relaxed mt-4 mb-8"
          style={{
            backgroundColor: 'rgba(168, 85, 247, 0.10)',
            border: '1px solid rgba(168, 85, 247, 0.25)',
            color: 'var(--rush-text-secondary, #cbd5e1)',
          }}
        >
          <span style={{ color: 'rgb(168, 85, 247)', fontWeight: 700 }}>INDUSTRY INSIGHT</span>
          {room.roundReveal.industryContextA && (
            <p className="mt-2">
              <strong style={{ color: 'var(--rush-text-primary)' }}>{room.currentQuestion.stockA.name}</strong>
              {' '}&mdash; {room.roundReveal.industryContextA}
            </p>
          )}
          {room.roundReveal.industryContextB && (
            <p className="mt-2">
              <strong style={{ color: 'var(--rush-text-primary)' }}>{room.currentQuestion.stockB.name}</strong>
              {' '}&mdash; {room.roundReveal.industryContextB}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
